import type {
  OverlayAsyncControllerComponent,
  OverlayAsyncControllerProps,
  OverlayControllerComponent,
} from './context/provider/ContentOverlayController';
import { createUseExternalEvents } from './utils/create-use-external-events';
import { randomId } from './utils/random-id';

export type OverlayEvent = {
  open: (args: {
    controller: OverlayControllerComponent;
    overlayId: string;
    componentKey: string;
    exitDuration: number;
  }) => void;
  close: (overlayId: string) => void;
  closeAll: () => void;
};

type OpenOverlayOptions = {
  overlayId?: string;
  exitDuration?: number;
};

const DEFAULT_EXIT_DURATION = 0;

export const createOverlay = (overlayId: string) => {
  const [useOverlayEvent, createEvent] = createUseExternalEvents<OverlayEvent>(
    `${overlayId}/overlay-kit`,
  );

  const open = (controller: OverlayControllerComponent, options?: OpenOverlayOptions) => {
    const overlayId = options?.overlayId ?? randomId();
    const componentKey = randomId();
    const exitDuration = options?.exitDuration ?? DEFAULT_EXIT_DURATION;
    const dispatchOpenEvent = createEvent('open');

    dispatchOpenEvent({ controller, overlayId, componentKey, exitDuration });
    return overlayId;
  };

  const openAsync = async <T>(
    controller: OverlayAsyncControllerComponent<T>,
    options?: OpenOverlayOptions,
  ) => {
    return new Promise<T>((_resolve, _reject) => {
      open((overlayProps, ...deprecatedLegacyContext) => {
        const close = (param: T) => {
          _resolve(param);
          overlayProps.close();
        };

        const reject = (reason?: unknown) => {
          _reject(reason);
          overlayProps.close();
        };

        const props: OverlayAsyncControllerProps<T> = { ...overlayProps, close, reject };
        return controller(props, ...deprecatedLegacyContext);
      }, options);
    });
  };

  const close = createEvent('close');
  const closeAll = createEvent('closeAll');

  return { open, openAsync, close, closeAll, useOverlayEvent };
};
